import React, { useState, useEffect } from 'react';
import { 
  Form, Button, Alert, Spinner, 
  Card, Row, Col, InputGroup 
} from 'react-bootstrap';
import { 
  FaTag, FaPercentage, FaDollarSign, 
  FaSave, FaArrowLeft 
} from 'react-icons/fa';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const AddEditCoupon = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [coupon, setCoupon] = useState({
    code: '',
    discount: '',
    type: 'percentage',
    isUsed: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isEdit) {
      const fetchCoupon = async () => {
        try {
          setLoading(true);
          const token = localStorage.getItem('adminToken');
          const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/coupons/${id}`, {
            headers: { 'x-auth-token': token }
          });
          setCoupon(response.data);
        } catch (err) {
          console.error('Error fetching coupon:', err);
          setError(err.response?.data?.error || 'Failed to load coupon');
        } finally {
          setLoading(false);
        }
      };
      fetchCoupon();
    }
  }, [id, isEdit]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCoupon(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validate discount based on type
    const discountValue = parseFloat(coupon.discount);
    if (coupon.type === 'percentage' && (discountValue <= 0 || discountValue > 100)) {
      setError('Percentage discount must be between 0 and 100');
      setIsSubmitting(false);
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      
      if (isEdit) {
        await axios.put(`${process.env.REACT_APP_API_URL}/admin/coupons/${id}`,
          coupon,
          { headers: { 'x-auth-token': token } }
        );
        toast.success('Coupon updated successfully');
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/admin/coupons`,
          coupon,
          { headers: { 'x-auth-token': token } }
        );
        toast.success('Coupon added successfully');
      }
      
      navigate('/admin/coupons');
    } catch (err) {
      console.error('Error saving coupon:', err);
      setError(err.response?.data?.error || 'Failed to save coupon');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="coupon-form-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{isEdit ? 'Edit Coupon' : 'Add New Coupon'}</h2>
        <Button 
          variant="secondary" 
          onClick={() => navigate('/admin/coupons')}
        >
          <FaArrowLeft className="me-2" />
          Back to Coupons
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Coupon Code</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaTag />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="code"
                      value={coupon.code}
                      onChange={handleChange}
                      placeholder="Enter coupon code"
                      required
                      disabled={isEdit} // Don't allow changing code for existing coupons
                    />
                  </InputGroup>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Discount Value</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      {coupon.type === 'percentage' ? (
                        <FaPercentage />
                      ) : (
                        <FaDollarSign />
                      )}
                    </InputGroup.Text>
                    <Form.Control
                      type="number"
                      name="discount"
                      value={coupon.discount}
                      onChange={handleChange}
                      placeholder={coupon.type === 'percentage' ? '0-100' : 'Enter amount'}
                      min="0"
                      max={coupon.type === 'percentage' ? '100' : undefined}
                      step="0.01"
                      required
                    />
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Discount Type</Form.Label>
                  <Form.Select
                    name="type"
                    value={coupon.type}
                    onChange={handleChange}
                    required
                  >
                    <option value="percentage">Percentage Discount</option>
                    <option value="fixed">Fixed Amount Discount</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="isUsed"
                    value={coupon.isUsed}
                    onChange={(e) => setCoupon({...coupon, isUsed: e.target.value === 'true'})}
                    required
                  >
                    <option value="false">Active</option>
                    <option value="true">Used</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end">
              <Button 
                variant="primary" 
                type="submit" 
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FaSave className="me-2" />
                    {isEdit ? 'Update Coupon' : 'Add Coupon'}
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AddEditCoupon;